/**
 * Material UI wired to the OPOC.us design tokens.
 *
 * Colours come from `tokens.ts`, which transcribes the brand skill's generated `tokens.css` once.
 * Styles read `token()` — a custom property with the brand value as its fallback — so restyling
 * through the stylesheet works. Material UI's *palette* reads `literalFor()` instead, because MUI
 * computes light, dark and contrast variants from each palette colour and cannot parse `var()`.
 *
 * That split is also how light and dark work. `tokens.css` redefines the custom properties under
 * `[data-theme="dark"]`, so every `token()` in this file and in every component flips on its own;
 * only the palette below has to be told which scheme it is in. Getting that wrong is what made the
 * first dark rendering unreadable — the surfaces flipped and the text colours did not.
 *
 * Three brand rules are enforced structurally here rather than by convention:
 *   1. Ink is #141414, never #000000. Even the shadow stack is ink-based.
 *   2. Yellow #F3F160 is a fill behind dark ink. It appears only in `emphasis` and `status.warning`,
 *      and is never offered as a text, border or chart-series colour.
 *   3. The five categorical chart slots are taken in order and never cycled.
 */
import { createElement, type ReactNode } from 'react';
import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

import { APP_TOKENS, type ColorMode, literal, literalFor, tint, token } from '@/theme/tokens';

export { soften, tint } from '@/theme/tokens';

export const brand = {
  navy: token('color-brand-navy'),
  blue: token('color-brand-blue'),
  cyan: token('color-brand-cyan'),
  violet: token('color-brand-violet'),
  green: token('color-brand-green'),
  red: token('color-brand-red'),
  /** The single accent. A fill behind dark ink — the primary button — never ink or a line. */
  yellow: token('color-brand-yellow'),
} as const;

export const semantic = {
  ink: token('color-semantic-ink'),
  inkMuted: token('color-semantic-ink-muted-aa'),
  inkSubtle: token('color-semantic-ink-subtle'),
  inkOnDark: token('color-semantic-ink-on-dark'),
  /** For the navy rail and anything else that stays a brand surface in both schemes. */
  inkOnBrand: token('color-semantic-ink-on-brand'),
  interactive: token('color-semantic-interactive'),
  interactiveHover: token('color-semantic-interactive-hover'),
  border: token('color-semantic-border-hairline'),
  borderStrong: token('color-semantic-border-strong'),
  focus: token('color-semantic-focus-ring'),
} as const;

export const surface = {
  page: token('color-surface-page'),
  raised: token('color-surface-raised'),
  sunken: token('color-surface-sunken'),
  inverse: token('color-surface-inverse'),
  /** A navy field on a page: covers, sign-in panels, tooltips. #314B73 under a dark scheme. */
  inverseBrand: token('color-surface-inverse-brand'),
  /** The tool sidebar: navy on light, the deep sunken navy on dark (guide: `--app-side`). */
  side: token('color-app-side'),
} as const;

/** Status colours. Reserved — never a chart series, always paired with an icon and a label. */
export const status = {
  good: token('color-dataviz-status-light-good'),
  warning: token('color-dataviz-status-light-warning'),
  serious: token('color-dataviz-status-light-serious'),
  critical: token('color-dataviz-status-light-critical'),
  /** Use this, not `critical`, when the status is small text rather than a mark. */
  criticalText: token('color-app-critical-text'),
} as const;

/** The recommended option, and nothing else. A fill behind ink — never text, a line or a border. */
export const emphasis = {
  fill: token('color-dataviz-emphasis-fill'),
  tint: token('color-dataviz-emphasis-tint'),
  tintEdge: token('color-dataviz-emphasis-tint-edge'),
  ink: token('color-dataviz-emphasis-ink'),
} as const;

/** Five categorical slots, in order. A sixth series folds to "Other" or becomes facets. */
export const categorical = [
  token('color-dataviz-categorical-light-1'),
  token('color-dataviz-categorical-light-2'),
  token('color-dataviz-categorical-light-3'),
  token('color-dataviz-categorical-light-4'),
  token('color-dataviz-categorical-light-5'),
] as const;

export const radius = {
  sm: token('radius-sm'),
  md: token('radius-md'),
  lg: token('radius-lg'),
  /** The site's own card radius. */
  xl: token('radius-xl'),
  pill: token('radius-pill'),
} as const;

/** Three durations and two curves (brand: motion). Leaving is always quicker than arriving. */
export const motion = {
  fast: token('motion-fast'),
  base: token('motion-base'),
  slow: token('motion-slow'),
  easing: token('motion-easing'),
  enter: token('motion-easing-enter'),
  exit: token('motion-easing-exit'),
} as const;

/** The brand's three line spacings: display, titles, and everything read as text. */
export const lineHeight = {
  tight: Number(literal('typography-line-height-tight')),
  snug: Number(literal('typography-line-height-snug')),
  normal: Number(literal('typography-line-height-normal')),
} as const;

const FONT_BRAND = `'Suisse Intl', Aptos, Arial, -apple-system, BlinkMacSystemFont, sans-serif`;
/** The brand's face for client deliverables, with its own fallbacks (brand: typography). */
export const FONT_DOCUMENT = `Raleway, Aptos, Arial, sans-serif`;

const shared: ThemeOptions = {
  shape: { borderRadius: 10 },
  spacing: 4,
  typography: {
    fontFamily: FONT_BRAND,
    // Tracking follows the brand scale: -0.05em titles, -0.03em body, 0 for numerals and caps.
    h1: { fontSize: '2.25rem', fontWeight: 600, letterSpacing: '-0.05em', lineHeight: 1.05 },
    // The page title (rendered as <h1> by PageHeader): the tool's .app-title.
    h2: { fontSize: '1.75rem', fontWeight: 500, letterSpacing: '-0.04em', lineHeight: 1.1 },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.05em', lineHeight: 1.2 },
    h4: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.2 },
    h5: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.03em' },
    h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.03em' },
    subtitle1: { fontSize: '1rem', letterSpacing: '-0.03em' },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, letterSpacing: 0 },
    // Tool views read at 17px / 1.55 (the guide's .opoc-app root); tables and dense lists at 14px.
    body1: { fontSize: '1.0625rem', letterSpacing: '-0.03em', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', letterSpacing: '-0.03em', lineHeight: 1.45 },
    button: { fontSize: '0.875rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' },
    caption: { fontSize: '0.8125rem', letterSpacing: 0, lineHeight: 1.4 },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
  },
};

/**
 * A notice's icon as the guide draws it (app-notice): a small ring in the status colour holding
 * "i" or "!", in place of Material's filled severity icons.
 */
function noticeGlyph(color: string, glyph: string): ReactNode {
  return createElement(
    'span',
    {
      'aria-hidden': true,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '50%',
        border: `1.5px solid ${color}`,
        color: semantic.ink,
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1,
      },
    },
    glyph,
  );
}

const componentOverrides = (theme: Theme, mode: ColorMode): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      // The roles `tokens.css` does not carry. Declaring them here — rather than editing the
      // brand's generated stylesheet, which stays a verbatim copy — keeps `token()` working for
      // them and switches them with the scheme like every other custom property.
      ':root': Object.fromEntries(
        APP_TOKENS.map((name) => [`--opoc-${name}`, literalFor(mode, name)]),
      ),
      // Never reproduce opoc.us's viewport root font size: it breaks text zoom (WCAG 1.4.4).
      html: { fontSize: '100%', WebkitFontSmoothing: 'antialiased' },
      body: {
        backgroundColor: surface.sunken,
        color: semantic.ink,
        fontVariantLigatures: 'common-ligatures',
      },
      // Numerals line up in every table without per-cell effort.
      'th, td, .opoc-numeric': { fontVariantNumeric: 'tabular-nums' },
      ':focus-visible': { outline: `2px solid ${semantic.focus}`, outlineOffset: '3px' },
      '@media (prefers-reduced-motion: reduce)': {
        '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      // Every tool control is 44px tall; a small one, for an action in a table row, is 32px.
      root: {
        borderRadius: radius.pill,
        paddingInline: '18px',
        minHeight: 44,
        // Disabled looks like the button it is, faded — not Material's grey box (guide: buttons).
        '&.Mui-disabled': { opacity: 0.38 },
      },
      sizeSmall: { minHeight: 32, fontSize: '0.8125rem', paddingInline: '14px' },
      // The guide's primary: yellow behind dark ink, one per screen, turning brand blue on hover
      // and focus. Solid blue is a different variant there — outbound or high-commitment — and
      // is reached with color="secondary".
      containedPrimary: {
        backgroundColor: brand.yellow,
        color: literalFor('light', 'color-semantic-ink'),
        '&.Mui-disabled': {
          backgroundColor: brand.yellow,
          color: literalFor('light', 'color-semantic-ink'),
        },
        '&:hover, &:focus-visible': {
          backgroundColor: brand.blue,
          color: semantic.inkOnBrand,
        },
      },
      containedSecondary: {
        backgroundColor: semantic.interactive,
        color: semantic.inkOnDark,
        '&:hover': { backgroundColor: semantic.interactiveHover },
      },
      // Secondary in the guide: a hairline in ink, filling with ink on hover.
      outlined: {
        borderColor: semantic.ink,
        color: semantic.ink,
        '&:hover': {
          backgroundColor: semantic.ink,
          color: literalFor(mode, 'color-surface-page'),
          borderColor: semantic.ink,
        },
      },
      // Destructive in the guide: red, filling red with white text on hover. Without this the
      // ink hairline above won over color="error", and every Delete read as an ordinary button
      // (ticket 197, 2026-10-30).
      outlinedError: {
        borderColor: status.critical,
        color: status.criticalText,
        '&:hover': {
          backgroundColor: status.critical,
          color: semantic.inkOnDark,
          borderColor: status.critical,
        },
      },
      text: { color: semantic.interactive },
    },
  },
  MuiIconButton: { styleOverrides: { root: { color: semantic.ink } } },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        border: `1px solid ${semantic.border}`,
        borderRadius: radius.md,
      },
      outlined: { borderColor: semantic.border },
    },
  },
  // A card is the guide's 30px block; Paper stays at 16px for groups, panels and menus.
  MuiCard: { styleOverrides: { root: { backgroundColor: surface.page, borderRadius: radius.xl } } },
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'transparent' },
    styleOverrides: {
      root: {
        backgroundColor: surface.page,
        borderBottom: `1px solid ${semantic.border}`,
        borderRadius: 0,
      },
    },
  },
  MuiDrawer: {
    // The rail is the tool sidebar: navy on light, the deep sunken navy on dark. Dark in both
    // schemes, so it carries the white lockup and its ink is `inkOnBrand`, which does not flip.
    styleOverrides: {
      paper: {
        backgroundColor: surface.side,
        color: semantic.inkOnBrand,
        border: 'none',
        borderRadius: 0,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      // Tags and status pills are 500, not 600 (guide: app-tag, app-status).
      root: { borderRadius: radius.pill, fontWeight: 500, letterSpacing: 0 },
      sizeSmall: { height: 22, fontSize: '0.75rem' },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      // The guide's tool table: hairline rows, no vertical rules, 14px cells at 1.35.
      root: {
        borderBottomColor: semantic.border,
        padding: '0.6rem 0.75rem',
        lineHeight: 1.35,
      },
      body: { fontSize: '0.875rem' },
      // Headers are small caps-style labels: 12px uppercase, tracked out, over a strong rule.
      head: {
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: semantic.inkMuted,
        padding: '0.5rem 0.75rem',
        borderBottom: `1px solid ${semantic.borderStrong}`,
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiTableRow: {
    // Zebra only in the sunken grey; a row lightens on hover (guide: tables).
    styleOverrides: {
      root: {
        transition: `background-color ${motion.fast} ${motion.easing}`,
        '&:nth-of-type(even)': { backgroundColor: surface.sunken },
        '&.MuiTableRow-hover:hover': { backgroundColor: surface.raised },
      },
    },
  },
  // The guide's own switch (the slide playlist on its strategy-deck page, `.pl-sw`): a 44 x 26 pill
  // the thumb sits inside, the AA muted ink when off, brand green when on, the page colour for
  // the thumb, and the focus ring around the track. Material's default track is a thin stripe
  // narrower than its thumb, which is hard to see against the page; the full-height track is what
  // makes the switch read as a control. A small switch is the same shape, a little smaller, for
  // lists. No shadow: the brand separates with colour rather than elevation. It moves at the fast
  // step with the standard curve; reduced motion stops it (CssBaseline).
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 44,
        height: 26,
        padding: 0,
        overflow: 'visible',
        '&.MuiSwitch-sizeSmall': {
          width: 36,
          height: 22,
          padding: 0,
          '& .MuiSwitch-switchBase': { padding: 3 },
          '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(14px)' },
          '& .MuiSwitch-thumb': { width: 16, height: 16 },
        },
      },
      switchBase: {
        padding: 3,
        transitionDuration: motion.fast,
        transitionTimingFunction: motion.easing,
        '&.Mui-checked': { color: surface.page, transform: 'translateX(18px)' },
        '&.Mui-checked + .MuiSwitch-track': { backgroundColor: brand.green, opacity: 1 },
        '&.Mui-focusVisible + .MuiSwitch-track': {
          outline: `2px solid ${semantic.focus}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled + .MuiSwitch-track, &.Mui-checked.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.38,
        },
        '&.Mui-disabled .MuiSwitch-thumb': { color: surface.page },
      },
      thumb: { width: 20, height: 20, color: surface.page, boxShadow: 'none' },
      track: {
        borderRadius: radius.pill,
        backgroundColor: semantic.inkMuted,
        opacity: 1,
        transition: `background-color ${motion.fast} ${motion.easing}`,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { height: 6, borderRadius: radius.pill, backgroundColor: surface.sunken },
      bar: { borderRadius: radius.pill },
    },
  },
  // Fields follow the guide's form-field spec: the label sits above the field rather than
  // floating inside it, the border is a hairline drawn inside a 30px-radius box, and focus
  // thickens that border to brand blue without moving anything on the page.
  MuiTextField: {
    defaultProps: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } },
  },
  MuiOutlinedInput: {
    defaultProps: { notched: false },
    styleOverrides: {
      root: {
        backgroundColor: surface.page,
        borderRadius: radius.xl,
        minHeight: 44,
        '& fieldset': { borderColor: semantic.borderStrong, top: 0 },
        '& legend': { display: 'none' },
        '&:hover fieldset': { borderColor: semantic.inkSubtle },
        '&.Mui-focused fieldset': { borderColor: semantic.interactive, borderWidth: 2 },
        '&.Mui-error fieldset': { borderWidth: 2 },
      },
      input: {
        paddingInline: 20,
        '&::placeholder': { color: semantic.inkMuted, opacity: 1 },
      },
      multiline: { paddingInline: 20 },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        transform: 'none',
        marginBottom: 6,
        // The tool's field label: 13px / 500 above the control.
        fontSize: '0.8125rem',
        fontWeight: 500,
        letterSpacing: 0,
        color: semantic.ink,
        '&.Mui-focused': { color: semantic.ink },
        '&.Mui-error': { color: semantic.ink },
      },
      asterisk: { color: semantic.inkMuted },
    },
  },
  MuiFormHelperText: {
    styleOverrides: { root: { marginInline: 0, fontSize: '0.8125rem', letterSpacing: 0 } },
  },
  MuiTooltip: {
    styleOverrides: {
      // The guide's inline definition: white on a navy field, 13px / 1.45, 10px radius.
      tooltip: {
        backgroundColor: surface.inverseBrand,
        color: semantic.inkOnBrand,
        fontSize: '0.8125rem',
        lineHeight: 1.45,
        borderRadius: radius.sm,
        padding: '12px 16px',
        maxWidth: 'min(20rem, 70vw)',
      },
      arrow: { color: surface.inverseBrand },
    },
  },
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        info: noticeGlyph(semantic.interactive, 'i'),
        success: noticeGlyph(status.good, 'i'),
        warning: noticeGlyph(status.serious, '!'),
        error: noticeGlyph(status.critical, '!'),
      },
    },
    styleOverrides: {
      // The guide's notice: a raised surface with a hairline, 16px radius, ink text whatever its
      // severity. Never a tinted background or a coloured left bar; the icon carries the status.
      root: {
        borderRadius: radius.md,
        border: `1px solid ${semantic.border}`,
        padding: '10px 16px',
        fontSize: '0.875rem',
        lineHeight: 1.45,
      },
      standardSuccess: { color: semantic.ink, backgroundColor: surface.raised },
      standardError: { color: semantic.ink, backgroundColor: surface.raised },
      standardWarning: { color: semantic.ink, backgroundColor: surface.raised },
      standardInfo: { color: semantic.ink, backgroundColor: surface.raised },
      icon: ({ ownerState }) => ({
        color: {
          success: status.good,
          error: status.critical,
          warning: status.serious,
          info: semantic.interactive,
        }[ownerState.severity ?? 'success'],
      }),
    },
  },
  MuiDialog: { styleOverrides: { paper: { borderRadius: radius.md } } },
  MuiDialogTitle: { styleOverrides: { root: { ...theme.typography.h4, paddingBottom: 8 } } },
  MuiTabs: { styleOverrides: { indicator: { backgroundColor: semantic.interactive, height: 2 } } },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '-0.03em',
        minHeight: 44,
        color: semantic.inkMuted,
        '&.Mui-selected': { color: semantic.ink },
      },
    },
  },
  MuiLink: {
    defaultProps: { underline: 'hover' },
    styleOverrides: { root: { color: semantic.interactive, fontWeight: 500 } },
  },
  // A skeleton has to sit *above* the card it stands in: sunken reads on white, raised on navy.
  // Loading reads as pill bars with a slow shimmer (guide: app-states).
  MuiSkeleton: {
    defaultProps: { animation: 'wave' },
    styleOverrides: {
      root: { backgroundColor: mode === 'dark' ? surface.raised : surface.sunken },
      text: { borderRadius: radius.pill, transform: 'none', height: '0.8rem', margin: '4px 0' },
    },
  },
});

export function buildTheme(mode: ColorMode): Theme {
  // The palette takes literals: Material UI derives light/dark/contrast variants from each colour
  // and cannot parse a custom property. Everything else in the theme uses `token()`, which the
  // stylesheet has already switched — so every entry here reads the *same* token name for both
  // schemes and lets `literalFor` pick the value. That is what keeps the two in step.
  const value = (name: Parameters<typeof literalFor>[1]): string => literalFor(mode, name);

  const base = createTheme({
    ...shared,
    palette: {
      mode,
      primary: {
        main: value('color-semantic-interactive'),
        // On #93BCFF the contrast text is ink, not white: 8.31:1 against 1.82:1.
        contrastText: value('color-semantic-ink-on-dark'),
      },
      secondary: {
        main: literalFor('light', 'color-brand-navy'),
        contrastText: literalFor('light', 'color-semantic-ink-on-dark'),
      },
      error: { main: value('color-dataviz-status-light-critical') },
      warning: { main: value('color-dataviz-status-light-serious') },
      success: { main: value('color-dataviz-status-light-good') },
      // Info is the interactive blue in the guide's tool vocabulary ("In progress"), not cyan,
      // which is never text on a light surface.
      info: { main: value('color-semantic-interactive') },
      text: {
        primary: value('color-semantic-ink'),
        secondary: value('color-semantic-ink-muted-aa'),
        disabled: value('color-semantic-ink-subtle'),
      },
      background: {
        default: value('color-surface-sunken'),
        paper: value('color-surface-page'),
      },
      divider: value('color-semantic-border-hairline'),
      action: {
        hover: value('color-semantic-border-hairline'),
        selected: value('color-semantic-border-hairline'),
        // Material UI's default disabled ink is rgba(0,0,0,0.26) — pure black, which the brand does
        // not use anywhere. Disabled controls are exempt from the contrast minimum; they are not
        // exempt from the palette.
        disabled: value('color-semantic-ink-subtle'),
        disabledBackground: value('color-semantic-border-hairline'),
      },
    },
  });

  return createTheme(base, {
    components: componentOverrides(base, mode),
    // Elevation is a hairline plus one soft ink shadow. Ink here means the light #141414 in both
    // schemes: a shadow is an absence of light, so it does not invert with the surface.
    shadows: [
      'none',
      `0 10px 20px ${tint('color-semantic-ink', 0.1)}`,
      `0 10px 20px ${tint('color-semantic-ink', 0.1)}`,
      ...Array<string>(22).fill(`0 15px 15px ${tint('color-semantic-ink', 0.06)}`),
    ] as Theme['shadows'],
  });
}

const VARIANTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'caption',
  'overline',
] as const;

/**
 * Each variant's line spacing on the brand's three steps. A button keeps its own: it sets the
 * control's height.
 */
const LINE_HEIGHTS: Partial<Record<(typeof VARIANTS)[number], number>> = {
  h1: lineHeight.tight,
  h2: lineHeight.snug,
  h3: lineHeight.snug,
  h4: lineHeight.snug,
  h5: lineHeight.snug,
  h6: lineHeight.snug,
  subtitle1: lineHeight.normal,
  subtitle2: lineHeight.normal,
  body1: lineHeight.normal,
  body2: lineHeight.normal,
  caption: lineHeight.normal,
  overline: lineHeight.normal,
};

/**
 * The theme presentation creation and employee packet creation are set in: Raleway with lining
 * figures, because both make client deliverables and the guide sets those in it, and every line
 * spacing on the brand's three steps. Material UI computes each variant when the theme is built,
 * so the face is set on each one rather than once; dialogs and menus, which leave the page through
 * a portal, take it from here too.
 */
export function documentFaceTheme(outer: Theme): Theme {
  return createTheme(outer, {
    typography: {
      fontFamily: FONT_DOCUMENT,
      ...Object.fromEntries(
        VARIANTS.map((variant) => [
          variant,
          {
            fontFamily: FONT_DOCUMENT,
            fontFeatureSettings: '"lnum" 1',
            ...(LINE_HEIGHTS[variant] ? { lineHeight: LINE_HEIGHTS[variant] } : {}),
          },
        ]),
      ),
    },
  });
}
